/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import { useEffect, useState } from "react";

/**
 * @description The restaurant group id carried alongside a navigated restaurant. Dropdown items
 * expose it nested or flat depending on the endpoint, so both shapes are read -- the same fallback
 * the Audit Logs page uses when it filters its own restaurant list.
 */
export const navigatedRestaurantGroupId = (navigatedRestaurant) => (
  typeof navigatedRestaurant === "object"
    ? navigatedRestaurant?.restaurant_group?.id ?? navigatedRestaurant?.restaurant_group_id ?? null
    : null
);

/**
 * @description The restaurant group name carried alongside a navigated restaurant, so a page can
 * label a group that has not been paged into its own dropdown yet.
 */
export const navigatedRestaurantGroupName = (navigatedRestaurant) => (
  typeof navigatedRestaurant === "object" ? navigatedRestaurant?.restaurant_group?.name ?? null : null
);

/**
 * @description True when the operator reached the page through a restaurant-carrying navigation.
 */
export const hasNavigatedRestaurant = (navigatedRestaurant) => Boolean(
  typeof navigatedRestaurant === "object"
    ? navigatedRestaurant?.id || navigatedRestaurant?.name
    : navigatedRestaurant
);

/**
 * @description Pre-fills and applies a module page's restaurant filter when the operator arrived
 * from another screen (the Audit Log import modal's "View Details", for example). Callers that
 * navigate cannot always resolve an id -- the audit modal falls back to a bare restaurant name when
 * the row's restaurant is not in its dropdown cache -- so the name is pushed into the restaurant
 * search until the dropdown returns a match, then swapped for the real id.
 *
 * The restaurant group is pre-filled alongside the restaurant because these pages only load their
 * restaurant dropdown once a group is selected: without a group, a name can never be resolved.
 * @param navigatedRestaurant location.state.restaurant: {id, name, restaurant_group}, {name} or a
 * plain name string
 * @param emptyFilters the page's cleared filter shape, so only the restaurant fields are pre-filled
 * @param restaurantOptionsCache the page's accumulated restaurant dropdown options
 * @param restaurantSearch current restaurant search term, so the hook only clears its own
 * @param setFilters,setRestaurantSearch,applySelectedFilters the page's setters
 * @returns the resolved identity plus prefillPending, which pages use to hold off their
 * single-option auto-apply only while a prefill can still land
 */
export const useNavigatedRestaurantFilter = ({
  navigatedRestaurant,
  emptyFilters,
  restaurantOptionsCache,
  restaurantSearch,
  setFilters,
  setRestaurantSearch,
  applySelectedFilters,
}) => {
  const navigatedRestaurantId = typeof navigatedRestaurant === "object" ? navigatedRestaurant?.id : null;
  const navigatedGroupId = navigatedRestaurantGroupId(navigatedRestaurant);
  const navigatedRestaurantName = typeof navigatedRestaurant === "object"
    ? navigatedRestaurant?.name
    : navigatedRestaurant || "";

  const [prefillRestaurantName, setPrefillRestaurantName] = useState(navigatedRestaurantName);

  useEffect(() => {
    if (!navigatedRestaurantId && !navigatedRestaurantName) {
      return;
    }

    const filter = {
      ...emptyFilters,
      restaurant_group_id: navigatedGroupId ?? emptyFilters.restaurant_group_id,
      restaurant_ids: navigatedRestaurantId ? [navigatedRestaurantId] : [],
    };

    setFilters(filter);
    // Only arm the name-resolution pass when there is no id. With an id the filter is applied
    // below, and letting the second effect fire again would re-run applySelectedFilters and reset
    // the operator's page and page size a beat after they landed.
    setPrefillRestaurantName(navigatedRestaurantId ? "" : navigatedRestaurantName);

    if (navigatedRestaurantName) {
      setRestaurantSearch(navigatedRestaurantName);
    }

    if (navigatedRestaurantId) {
      applySelectedFilters(filter);
    }
  }, [navigatedGroupId, navigatedRestaurantId, navigatedRestaurantName]);

  useEffect(() => {
    if (!prefillRestaurantName) {
      return;
    }

    const matchedRestaurant = restaurantOptionsCache?.find((restaurant) => (
      String(restaurant?.id) === String(navigatedRestaurantId) || restaurant?.name === prefillRestaurantName
    ));

    if (!matchedRestaurant) {
      return;
    }

    const filter = {
      ...emptyFilters,
      // Prefer the group the matched option reports; the navigation may not have carried one.
      restaurant_group_id: navigatedRestaurantGroupId(matchedRestaurant)
        ?? navigatedGroupId
        ?? emptyFilters.restaurant_group_id,
      restaurant_ids: [matchedRestaurant.id],
    };

    setFilters(filter);
    applySelectedFilters(filter);

    if (restaurantSearch === prefillRestaurantName) {
      setRestaurantSearch("");
    }

    setPrefillRestaurantName("");
  }, [navigatedGroupId, navigatedRestaurantId, prefillRestaurantName, restaurantOptionsCache, restaurantSearch]);

  return {
    navigatedRestaurantId,
    navigatedRestaurantName,
    navigatedGroupId,
    prefillPending: Boolean(prefillRestaurantName),
  };
};
